import {createStore, del, entries, get, set, clear} from 'idb-keyval'

// TODO: should this actually be using '@github-ui/stats' ?
import type {SendAnalyticsEventFunction} from '@github-ui/use-analytics'
import {getCurrentUserLogin} from '@github-ui/client-env'
import {trackStorage} from './track-storage'
import {isFeatureEnabled} from '@github-ui/feature-flags'

interface Validator {
  Check(value: unknown): boolean
  Code(): string
  Errors(value: unknown): unknown[]
}

type Props = {
  validator: Validator
  sendAnalyticsEvent: SendAnalyticsEventFunction
  storeName?: string
}

export const CACHE_EVENTS = {
  CACHE_HIT: 'offline_cache.cache_hit',
  CACHE_MISS: 'offline_cache.cache_miss',
  CACHE_EVICT: 'offline_cache.cache_evict',
  CACHE_INVALID: 'offline_cache.stale_structure',
  CACHE_EXPIRED: 'offline_cache.expired_item',
  CACHE_SET: 'offline_cache.cache_set',
  CACHE_TIMEOUT: 'offline_cache.cache_timeout',
  CLEANUP_STARTED: 'offline_cache.cleanup_started',
  CLEANUP_COMPLETED: 'offline_cache.cleanup_completed',
  CLEANUP_ERROR: 'offline_cache.cleanup_error',
  CLEANUP_ITEM_ERROR: 'offline_cache.cleanup_item_error',
  ENTRIES_READ: 'offline_cache.entries_read',
  ANY: 'offline_cache.*',
  NONE: 'offline_cache.none',
} as const

const INDEXEDDB_TIMEOUT_MS = 1000

const NOT_AVAILABLE = 'N/A'

/**
 * Error thrown when an IndexedDB operation times out
 */
class IndexedDbTimeoutError extends Error {
  constructor(operation: 'read' | 'write', key: string, timeoutMs: number) {
    super(`IndexedDB ${operation} operation timed out after ${timeoutMs}ms for key: ${key}`)
    this.name = 'IndexedDbTimeoutError'
  }
}

export type CacheEvent = (typeof CACHE_EVENTS)[keyof typeof CACHE_EVENTS]
export type ANY_CACHE_EVENT = typeof CACHE_EVENTS.ANY
export type NO_CACHE_EVENTS = typeof CACHE_EVENTS.NONE

export const DBNAME_PREFIX = 'offline_cache::'
export const CACHE_VERSION = 1
export const getDbName = () => DBNAME_PREFIX + getCurrentUserLogin()

type SafeIndexedDbPersister<TPersistedQuery> = {
  cleanupIntervalId: number | undefined
  isCleanupRunning: boolean
  getItem(key: string, attributes?: {[key: string]: unknown}): Promise<TPersistedQuery | undefined>
  setItem(
    key: string,
    value: TPersistedQuery,
    dataUpdatedAt?: number,
    ttl?: number,
    attributes?: {[key: string]: unknown},
  ): Promise<void>
  removeItem(key: string, attributes?: {[key: string]: unknown}): Promise<void>
  isBustedOrExpired(persistedQuery: TPersistedQuery): boolean
  entries(): Promise<Array<[string, TPersistedQuery]>>
  cleanupExpiredEntries(): Promise<void>
  stopPeriodicCleanup(): void
}

// Singleton cache to ensure only one persister instance per store
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const persisters = new Map<string, SafeIndexedDbPersister<any>>()

/**
 * Clears the singleton persister cache. Primarily used for testing.
 * Also stops any running cleanup intervals for all cached persisters.
 */
export function clearPersisterCache() {
  // Stop all cleanup intervals before clearing
  for (const persister of persisters.values()) {
    if (persister && typeof persister.stopPeriodicCleanup === 'function') {
      persister.stopPeriodicCleanup()
    }
  }
  persisters.clear()
}

export function createSafeIndexedDbPersister<
  TState extends {
    data: unknown | null
    dataUpdatedAt?: number
    ttl?: number
    preheatSource?: string
    cacheVersion?: number
  },
  TPersistedQuery extends {state: TState; queryKey: readonly unknown[]; queryHash: string},
>({validator, sendAnalyticsEvent, storeName = 'queries'}: Props) {
  // Create a unique key for this persister instance
  const persisterId = `${getDbName()}:${storeName}`

  // Return existing instance if available
  const existingPersister = persisters.get(persisterId)
  if (existingPersister) {
    return existingPersister
  }

  function isExpired(persistedQuery: TPersistedQuery) {
    if (persistedQuery.state.dataUpdatedAt && persistedQuery.state.ttl) {
      const queryAge = Date.now() - persistedQuery.state.dataUpdatedAt
      if (queryAge > persistedQuery.state.ttl) {
        return true
      }
    }
    if (persistedQuery.state.cacheVersion !== undefined && persistedQuery.state.cacheVersion !== CACHE_VERSION) {
      return true
    }

    // Treat queries without dataUpdatedAt or ttl as never expiring
    return false
  }

  const idbStore = createStore(getDbName(), storeName)

  /**
   * Checks if IndexedDB operations should be disabled via feature flag.
   * When disabled, read/write operations are blocked but cleanup still runs.
   */
  function isIndexedDbDisabled(): boolean {
    return isFeatureEnabled('disable-indexdb-operations')
  }

  /**
   * Wraps a promise with a timeout to prevent indefinite hangs in IndexedDB operations.
   * Rejects with IndexedDbTimeoutError if the operation times out.
   */
  async function withTimeout<T>(
    promise: Promise<T>,
    timeoutMs: number,
    operation: 'read' | 'write',
    key: string,
  ): Promise<T> {
    if (!isFeatureEnabled('issues_cache_operation_timeout')) {
      return promise
    }

    let timeoutId: ReturnType<typeof setTimeout> | null = null

    const timeoutPromise = new Promise<T>((_, reject) => {
      timeoutId = setTimeout(() => {
        sendAnalyticsEvent(CACHE_EVENTS.CACHE_TIMEOUT, key, {operation})
        reject(new IndexedDbTimeoutError(operation, key, timeoutMs))
      }, timeoutMs)
    })

    try {
      const result = await Promise.race([promise, timeoutPromise])
      // Clear timeout if promise resolves successfully
      if (timeoutId) clearTimeout(timeoutId)
      return result
    } catch (error) {
      // Clear timeout if promise rejects
      if (timeoutId) clearTimeout(timeoutId)
      throw error
    }
  }

  /**
   * Cleans up expired entries from the IndexedDB store.
   * Iterates through all entries and removes those that have exceeded their TTL.
   * Sends analytics events for tracking cleanup operations.
   * Includes concurrency guard to prevent overlapping cleanup operations.
   *
   * @returns Promise that resolves when cleanup is complete
   */
  async function cleanupExpiredEntries() {
    if (persister.isCleanupRunning) return
    persister.isCleanupRunning = true
    const startTime = Date.now()
    try {
      sendAnalyticsEvent(CACHE_EVENTS.CLEANUP_STARTED, '', {})

      const allEntries = await entries(idbStore)
      const totalEntries = allEntries.length
      let removedCount = 0

      for (const [key, value] of allEntries) {
        try {
          const item = value as TPersistedQuery
          if (isExpired(item)) {
            // Send cache_evict event with reason for tracking
            sendAnalyticsEvent(CACHE_EVENTS.CACHE_EVICT, key.toString(), {
              reason: 'periodic_cleanup',
            })
            // fire and forget don't await
            // eslint-disable-next-line github/no-then
            del(key, idbStore).catch(error => {
              sendAnalyticsEvent(CACHE_EVENTS.CLEANUP_ITEM_ERROR, key.toString(), {
                error: String(error),
              })
            })
            removedCount++
          }
        } catch (error) {
          // Log error for individual item but continue with cleanup
          sendAnalyticsEvent(CACHE_EVENTS.CLEANUP_ITEM_ERROR, key.toString(), {
            error: String(error),
          })
        }
      }

      sendAnalyticsEvent(CACHE_EVENTS.CLEANUP_COMPLETED, '', {
        removed_count: removedCount,
        total_entries: totalEntries,
        duration_ms: Date.now() - startTime,
      })
    } catch (error) {
      sendAnalyticsEvent(CACHE_EVENTS.CLEANUP_ERROR, '', {
        error: String(error),
      })
    } finally {
      persister.isCleanupRunning = false
    }
  }

  /**
   * Starts periodic cleanup of expired entries.
   * Uses requestIdleCallback for initial run (with setTimeout fallback),
   * then setInterval for recurring cleanup.
   *
   * @param intervalMs - Interval between cleanup runs in milliseconds (default: 60 minutes)
   */
  function startPeriodicCleanup(intervalMs: number = 60 * 60 * 1000) {
    if (typeof window === 'undefined') return // SSR guard

    // Schedule initial cleanup using requestIdleCallback with fallback
    const scheduleCleanup = () => {
      const windowWithIdleCallback = window as Window & {requestIdleCallback?: typeof requestIdleCallback}
      if (typeof windowWithIdleCallback.requestIdleCallback === 'function') {
        windowWithIdleCallback.requestIdleCallback(() => void cleanupExpiredEntries(), {timeout: 10000})
      } else {
        setTimeout(() => void cleanupExpiredEntries(), 10000)
      }
    }

    scheduleCleanup() // schedule first run

    // Set up recurring cleanup interval
    persister.cleanupIntervalId = window.setInterval(() => void cleanupExpiredEntries(), intervalMs)
  }

  /**
   * Stops periodic cleanup and clears the interval.
   * Useful for testing and cleanup.
   */
  function stopPeriodicCleanup() {
    if (persister.cleanupIntervalId !== undefined) {
      clearInterval(persister.cleanupIntervalId)
      persister.cleanupIntervalId = undefined
    }
  }

  const persister = {
    cleanupIntervalId: undefined as number | undefined,
    isCleanupRunning: false,
    async getItem(
      key: string,
      attributes?: {
        [key: string]: unknown
      },
    ) {
      // Return early if IndexedDB operations are disabled
      if (isIndexedDbDisabled()) {
        return
      }

      try {
        const item = await withTimeout(get<TPersistedQuery>(key, idbStore), INDEXEDDB_TIMEOUT_MS, 'read', key)
        // only hydrate persisted query data
        if (!item?.state?.data) {
          sendAnalyticsEvent(CACHE_EVENTS.CACHE_MISS, key.toString(), attributes)
          return
        }

        if (isExpired(item)) {
          this.removeItem(key)
          sendAnalyticsEvent?.('offline_cache.expired_item', key.toString(), attributes)
          return
        }

        // Validate the data inside the persisted query
        const valid = validator.Check(item.state.data)
        if (!valid) {
          sendAnalyticsEvent(CACHE_EVENTS.CACHE_INVALID, key.toString(), attributes)
          return
        }
        const age = item.state.dataUpdatedAt ? Date.now() - item.state.dataUpdatedAt : NOT_AVAILABLE
        const ttl = item.state.ttl ?? NOT_AVAILABLE
        sendAnalyticsEvent(CACHE_EVENTS.CACHE_HIT, key.toString(), {
          ...attributes,
          age,
          ttl,
          preheat_source: item.state.preheatSource,
        })
        return item
      } catch (error) {
        // Handle timeout gracefully by returning undefined (cache miss)
        if (error instanceof IndexedDbTimeoutError) {
          return undefined
        }
        // "Re-throw the caught exception" according to https://github.com/github/github-ui/blob/main/packages/failbot/failbot.ts
        throw error
      }
    },
    async setItem(
      key: string,
      value: TPersistedQuery,
      dataUpdatedAt?: number,
      ttl?: number,
      attributes?: {
        [key: string]: unknown
      },
    ) {
      // Return early if IndexedDB operations are disabled
      if (isIndexedDbDisabled()) {
        return Promise.resolve()
      }

      const effectiveTtl = ttl ?? value.state.ttl
      const valueToStore = {
        ...value,
        state: {
          ...value.state,
          dataUpdatedAt: dataUpdatedAt ?? value.state.dataUpdatedAt,
          ttl: effectiveTtl,
          cacheVersion: CACHE_VERSION,
        },
      }
      trackStorage(key, sendAnalyticsEvent)
      sendAnalyticsEvent(CACHE_EVENTS.CACHE_SET, key.toString(), {...attributes, ttl: effectiveTtl})

      try {
        await withTimeout(set(key, valueToStore, idbStore), INDEXEDDB_TIMEOUT_MS, 'write', key)
      } catch (error) {
        // Handle timeout gracefully - write operation will be skipped
        if (error instanceof IndexedDbTimeoutError) {
          return
        }
        throw error
      }
    },
    removeItem(key: string, attributes?: {[key: string]: unknown}) {
      sendAnalyticsEvent(CACHE_EVENTS.CACHE_EVICT, key.toString(), attributes)
      return del(key, idbStore)
    },
    // This should probably be removed once we handle all expiration logic here and not in clients
    isBustedOrExpired(persistedQuery: TPersistedQuery) {
      return isExpired(persistedQuery)
    },
    async entries() {
      const startTime = Date.now()
      const things = await entries(idbStore)
      const durationMs = Date.now() - startTime
      sendAnalyticsEvent(CACHE_EVENTS.ENTRIES_READ, '', {
        duration_ms: durationMs,
        entry_count: things.length,
      })
      return things as Array<[string, TPersistedQuery]>
    },
    cleanupExpiredEntries,
    stopPeriodicCleanup,
  }

  // Store the persister instance before returning
  persisters.set(persisterId, persister)

  // Auto-start periodic cleanup after persister is defined
  const shouldRunCleanUp = isFeatureEnabled('indexdb-offline-cache-periodic-cleanup')

  if (shouldRunCleanUp) {
    startPeriodicCleanup()
  }

  return persister
}

/**
 * Clears all persisted query cache data from IndexedDB for all users.
 * This function is called on logout to remove sensitive cached data.
 *
 * The function:
 * - Finds all IndexedDB databases matching the offline cache prefix
 * - Clears all object stores within those databases
 * - Handles errors gracefully without throwing
 *
 * @example
 * // Called automatically on logout via clear-data-on-logout.ts
 * await clearIndexedDBPersisterData()
 */
export async function clearIndexedDBPersisterData() {
  try {
    const databases = await window?.indexedDB?.databases?.()
    if (!databases) return

    // For each database matching our prefix, clear all its stores
    const clearPromises = databases
      .filter(db => db.name?.startsWith(DBNAME_PREFIX))
      .map(async db => {
        if (!db.name) return

        try {
          // Open the database to get all store names
          const openRequest = indexedDB.open(db.name)
          const database = await new Promise<IDBDatabase>((resolve, reject) => {
            openRequest.onsuccess = () => resolve(openRequest.result)
            openRequest.onerror = () => reject(openRequest.error)
          })

          // Get all object store names in this database
          const storeNames = Array.from(database.objectStoreNames)
          database.close()

          // Clear each store using idb-keyval
          const storeClearPromises = storeNames.map(async storeName => {
            if (!db.name) return
            try {
              const store = createStore(db.name, storeName)
              await clear(store)
            } catch {
              // Silently ignore errors when clearing individual stores
            }
          })

          await Promise.all(storeClearPromises)
        } catch {
          // Silently ignore errors when clearing individual databases
        }
      })

    await Promise.allSettled(clearPromises)
  } catch {
    // ignore failure to clear IndexedDB
  }
}
